import type { FC, MouseEventHandler, PropsWithChildren, ReactNode } from 'react';

import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';

import {
  contentRecipe,
  contentWithPaddingStyle,
  dialogPageGridStyle,
  overlayStyle,
  scrollContainerStyle,
  SharedProps,
} from './Dialog';
import { getClassNames } from '../../util/css';
import { RecipeVariants } from '../../vanilla-extract.css';
import { Box } from '../Box/Box';
import { Button } from '../Button/Button';
import { Heading } from '../Heading/Heading';
import { Stack } from '../Stack/Stack';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';

type AlertDialogVariants = RecipeVariants<typeof contentRecipe>;

type BaseProps = {
  /** Determines whether the user is able to interact (scroll) with the background */
  disableBackgroundInteraction?: boolean;
  /** Optional custom footer content to replace default buttons */
  footer?: ReactNode;
  /** Hidden description announced with the dialog. Ignored when `description` is set. */
  accessibleDescription?: string;
  /** Accessible name for the dialog title when it should differ from the visible heading. */
  accessibleName?: string;
  /** Override which element receives focus when the dialog opens. */
  onOpenAutoFocus?: (event: Event) => void;
  /** Override which element receives focus when the dialog closes. */
  onCloseAutoFocus?: (event: Event) => void;
} & SharedProps &
  AlertDialogVariants;

type Props = BaseProps &
  (
    | {
        footer: ReactNode;
        onConfirm?: never;
        onDeny?: never;
        confirmText?: never;
        denyText?: never;
      }
    | {
        /** Optional custom footer content to replace default buttons */
        footer?: never;
        /** Function that is triggerered when the user confirms the action */
        onConfirm: MouseEventHandler<HTMLButtonElement>;
        /** Function that is triggerered when the user denies the action */
        onDeny: MouseEventHandler<HTMLButtonElement>;
        /** Text that is shown on the button that confirms the action */
        confirmText: string;
        /** Text that is shown on the button that denies the action */
        denyText: string;
      }
  );

export const AlertDialog: FC<PropsWithChildren<Props>> = ({
  title,
  description,
  width = 'regular',
  isOpen,
  setOpen,
  children,
  onConfirm,
  onDeny,
  confirmText,
  denyText,
  trigger,
  footer = undefined,
  disableBackgroundInteraction = true,
  accessibleDescription,
  accessibleName,
  onOpenAutoFocus,
  onCloseAutoFocus,
}) => {
  const contentClassNames = getClassNames(contentRecipe({ width }), contentWithPaddingStyle);

  const renderOverlay = (children: ReactNode) =>
    disableBackgroundInteraction ? (
      <AlertDialogPrimitive.Overlay className={overlayStyle}>{children}</AlertDialogPrimitive.Overlay>
    ) : (
      <div className={overlayStyle}>{children}</div>
    );

  return (
    <AlertDialogPrimitive.Root onOpenChange={setOpen} open={isOpen}>
      {trigger ? <AlertDialogPrimitive.Trigger asChild>{trigger}</AlertDialogPrimitive.Trigger> : null}
      <AlertDialogPrimitive.Portal>
        {renderOverlay(
          <div className={dialogPageGridStyle}>
            <AlertDialogPrimitive.Content
              className={contentClassNames}
              onEscapeKeyDown={event => event.preventDefault()}
              onOpenAutoFocus={onOpenAutoFocus}
              onCloseAutoFocus={onCloseAutoFocus}>
              <Box className={scrollContainerStyle} overflow="auto">
                {title && (
                  <Box paddingBottom={{ initial: '2', md: '4' }}>
                    <AlertDialogPrimitive.Title asChild>
                      {typeof title === 'string' ? (
                        <Heading as="h2" size="S" aria-label={accessibleName}>
                          {title}
                        </Heading>
                      ) : (
                        title
                      )}
                    </AlertDialogPrimitive.Title>
                  </Box>
                )}
                {description && (
                  <AlertDialogPrimitive.Description asChild>
                    <Box paddingBottom={{ initial: '4', md: '5' }}>{description}</Box>
                  </AlertDialogPrimitive.Description>
                )}
                {!description && accessibleDescription ? (
                  <AlertDialogPrimitive.Description asChild>
                    <VisuallyHidden>{accessibleDescription}</VisuallyHidden>
                  </AlertDialogPrimitive.Description>
                ) : null}
                {children}
              </Box>
              <Box paddingTop="6">
                {footer ?? (
                  <Stack direction={{ initial: 'column', md: 'row' }} gap="5">
                    <AlertDialogPrimitive.Action asChild>
                      <Button onClick={onConfirm}>{confirmText}</Button>
                    </AlertDialogPrimitive.Action>
                    <AlertDialogPrimitive.Cancel asChild>
                      <Button onClick={onDeny} action="secondary">
                        {denyText}
                      </Button>
                    </AlertDialogPrimitive.Cancel>
                  </Stack>
                )}
              </Box>
            </AlertDialogPrimitive.Content>
          </div>,
        )}
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
};

AlertDialog.displayName = 'AlertDialog';
